import React, { useState } from "react";
import { Typography, Button, useTheme, useMediaQuery } from "@mui/material";
import { useTranslation } from "next-i18next";
import { ITagsData, ITags, IKeyFigureGroup } from "src/api/server/extended-search";
import { IconFilter } from "src/components/icons-mui/filter";
import { Container } from "src/components/layout/container";
import { MobileSearchFilter } from "src/components/pages/search/components/filter-section/mobile-search-filter/mobile-search-filter";
import { ISearchRequestParamsOmitCountry } from "src/components/pages/search/search.types";
import { useNumberFormatter } from "src/hooks/formatters/use-number-formatter";
import { useRouterCaller } from "src/utils/router/use-router-caller";
import { SearchFilterHeader } from "../search-filter-header";

interface IMobileSearchFilterContainerProps {
    resultCount: number | null;
    isLoading: boolean;
    tagsData: ITagsData;
    popularKeyFiguresGroup: IKeyFigureGroup;
    keyFiguresGroupsWithIsPopularIndication: IKeyFigureGroup[];
    popularTags: ITags;
    onSearchTermClear: () => void;
    onReset: () => void;
    onSearch: (params: ISearchRequestParamsOmitCountry) => void;
    searchTerm?: string | string[];
}

export const MobileSearchFilterContainer = ({
    searchTerm,
    resultCount,
    isLoading,
    tagsData,
    popularKeyFiguresGroup,
    keyFiguresGroupsWithIsPopularIndication,
    popularTags,
    onSearchTermClear,
    onReset,
    onSearch,
}: IMobileSearchFilterContainerProps) => {
    const { t } = useTranslation(["extended-search"]);
    const { formatNumber } = useNumberFormatter();
    const [isOpen, setIsOpen] = useState(false);
    const { isCallerContentApp } = useRouterCaller();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const hasResults = resultCount !== null;
    const displayedResultsCount = hasResults ? formatNumber(resultCount) : "-";
    const wikifoliosFound = resultCount === 1 ? t("wikifolio-found") : t("wikifolios-found");

    if (!isMobile) {
        return null;
    }

    return (
        <Container sx={{ backgroundColor: "gray.50", pt: 3, pb: 3, display: { xs: "block", sm: "none" } }}>
            {!isCallerContentApp && <SearchFilterHeader searchTerm={searchTerm} isMobile handleOnClear={onSearchTermClear} />}
            <Typography mb={2} fontSize="sm" display={{ xs: "block", sm: "none" }}>
                <Typography component="strong" fontWeight="fontWeightBold" fontSize="xl" mr={0.5}>
                    {displayedResultsCount}
                </Typography>
                {wikifoliosFound}
            </Typography>
            <Button startIcon={<IconFilter sx={{ width: 24, height: 24 }} />} onClick={() => setIsOpen(true)} fullWidth variant="contained">
                {t("buttons.filter")}
            </Button>
            <MobileSearchFilter
                isOpen={isOpen}
                isLoading={isLoading}
                resultCount={resultCount}
                tagsData={tagsData}
                popularKeyFiguresGroup={popularKeyFiguresGroup}
                keyFiguresGroups={keyFiguresGroupsWithIsPopularIndication}
                popularTags={popularTags}
                onClose={() => setIsOpen(false)}
                onReset={onReset}
                onSearch={onSearch}
            />
        </Container>
    );
};
